import apiClient from './apiService';

const BASE = '/api/v1/com';

export interface StatInstance {
  id: number;
  entity_id: number;
  filing_id: number;
  filing_name?: string | null;
  obligation_type?: string | null;
  frequency?: string | null;
  location_id?: number | null;
  location_name?: string | null;
  period_label?: string | null;
  due_date?: string | null;
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';
  initiated_by?: number | null;
  initiated_by_name?: string | null;
  initiated_on?: string | null;
  step_count: number;
  completed_count: number;
}

/** A document attached to a step as proof of compliance. */
export interface StatStepEvidence {
  id: number;
  file_id: number;
  caption?: string | null;
  attached_on?: string | null;
  file_name?: string | null;
  mime_type?: string | null;
  file_size_kb?: number | null;
  attached_by_name?: string | null;
}

export interface StatInstanceStep {
  id: number;
  instance_id: number;
  stat_step_id: number;
  step_number: number;
  authority_id: number;
  authority_name?: string | null;
  role: string;
  status: 'PENDING' | 'ACTIVE' | 'COMPLETED' | 'SKIPPED';
  completed_by?: number | null;
  completed_on?: string | null;
  notes?: string | null;
  skip_reason?: string | null;
  step_name?: string | null;
  /**
   * The date the obligation was ACTUALLY discharged — distinct from
   * completed_on, which is when the button was pressed. A filing remitted on
   * the 7th and signed off on the 20th has compliance_date = the 7th, and that
   * is the date an assessing officer asks about.
   */
  compliance_date?: string | null;
  /** Challan / receipt / acknowledgement number issued by the authority. */
  acknowledgement_ref?: string | null;
  /** Proof documents. Required on a performing step before it can complete. */
  evidence?: StatStepEvidence[];
  /** True only when the current user is assigned to this step's authority. */
  can_action?: boolean;
}

export interface StatInstanceDetail extends StatInstance {
  steps: StatInstanceStep[];
}

export const STAT_STATUS_COLOR: Record<string, string> = {
  PENDING:     'bg-gray-100 text-gray-500',
  IN_PROGRESS: 'bg-blue-100 text-blue-700',
  COMPLETED:   'bg-green-100 text-green-700',
  CANCELLED:   'bg-red-100 text-red-500',
};

export const STEP_STATUS_COLOR: Record<string, string> = {
  PENDING:   'bg-gray-100 text-gray-400',
  ACTIVE:    'bg-blue-100 text-blue-700',
  COMPLETED: 'bg-green-100 text-green-700',
  SKIPPED:   'bg-amber-100 text-amber-600',
};

export const ROLE_LABEL: Record<string, string> = {
  PERFORM: 'Perform',
  APPROVE: 'Approve',
  REVIEW:  'Review',
  NOTIFY:  'Notify',
  OTHER:   'Other',
};

/** Rollups from /com/dashboard/compliance-analytics. Every figure derives from
 *  the same two dates the register runs on — due_date and compliance_date — so
 *  these reconcile with the flat compliance-summary by construction. */
export interface ComplianceAgeing {
  overdue_30_plus: number; overdue_8_30: number; overdue_1_7: number;
  due_today: number; due_next_7: number; due_next_30: number; due_later: number;
}
export interface ComplianceTimeliness {
  closed_total: number; on_time: number; late: number; undated: number;
  /** Share of CLOSED obligations discharged on or before the due date. */
  on_time_pct: number | null;
  avg_days_early: number;
}
export interface PendingWithRow {
  seat: string; owner_name?: string | null; owner_login?: string | null;
  role: string; open_steps: number; overdue_steps: number; earliest_due?: string | null;
}
export interface ClassificationRow {
  label: string; total: number; completed: number;
  overdue: number; open_not_due: number;
  /** Closed, but discharged AFTER the due date — distinct from `overdue`. */
  late_closed: number;
}
export interface ComplianceAnalytics {
  ageing: ComplianceAgeing;
  timeliness: ComplianceTimeliness;
  pending_with: PendingWithRow[];
  classification: {
    by_law: ClassificationRow[];
    by_department: ClassificationRow[];
    by_criticality: ClassificationRow[];
    by_obligation_type: ClassificationRow[];
  };
}

const statutoryService = {
  getAnalytics: async (): Promise<ComplianceAnalytics> => {
    const res = await apiClient.get(`${BASE}/dashboard/compliance-analytics`);
    return res.data?.data ?? res.data;
  },

  listInstances: async (view: 'my_tasks' | 'my_initiated' | 'all' = 'my_tasks'): Promise<StatInstance[]> => {
    const res = await apiClient.get(`${BASE}/stat-instances`, { params: { view } });
    return res.data?.data ?? res.data ?? [];
  },

  getInstance: async (instId: number): Promise<StatInstanceDetail> => {
    const res = await apiClient.get(`${BASE}/stat-instances/${instId}`);
    return res.data?.data ?? res.data;
  },

  /**
   * Count of distinct obligation definitions in the register (COM_filings,
   * active only) — the same number the Obligation Register tab shows. This is
   * NOT the stat-instance (rollout) count: two obligations rolled out four
   * times give countObligations()=2 but listInstances('all').length=4.
   */
  countObligations: async (): Promise<number> => {
    const res = await apiClient.get(`${BASE}/obligations`, { params: { page: 1, limit: 1, status: 'ACTIVE' } });
    return res.data?.meta?.total ?? res.data?.total ?? 0;
  },

  cancelInstance: async (instId: number): Promise<void> => {
    await apiClient.post(`${BASE}/stat-instances/${instId}/cancel`);
  },

  completeStep: async (
    stepId: number,
    notes: string,
    complianceDate?: string,
    acknowledgementRef?: string,
  ): Promise<void> => {
    await apiClient.post(`${BASE}/stat-instance-steps/${stepId}/complete`, {
      notes,
      compliance_date: complianceDate ?? null,
      acknowledgement_ref: acknowledgementRef ?? null,
    });
  },

  // ── Proof of compliance ────────────────────────────────────────────────────
  // The file itself goes to the shared UTL_files store (same upload path as
  // audit evidence); these calls only bridge a stored file to the step.
  listEvidence: async (stepId: number): Promise<StatStepEvidence[]> => {
    const res = await apiClient.get(`${BASE}/stat-instance-steps/${stepId}/evidence`);
    return res.data?.data ?? [];
  },

  uploadProof: async (stepId: number, file: File, caption?: string): Promise<void> => {
    const fd = new FormData();
    fd.append('file', file);
    const up = await apiClient.post('/api/v1/utl/files', fd);
    const fileId = up.data?.data?.id ?? up.data?.id;
    if (!fileId) throw new Error('Upload succeeded but no file id was returned.');
    await apiClient.post(`${BASE}/stat-instance-steps/${stepId}/evidence`, {
      file_id: fileId,
      caption: caption ?? null,
    });
  },

  deleteEvidence: async (stepId: number, evidenceId: number): Promise<void> => {
    await apiClient.delete(`${BASE}/stat-instance-steps/${stepId}/evidence/${evidenceId}`);
  },

  /**
   * Fetch the proof as a blob and hand it to the browser. A plain <a href>
   * cannot be used: the route needs the Authorization header, so a bare link
   * would 401.
   */
  downloadEvidence: async (
    stepId: number, evidenceId: number, fileName?: string | null,
  ): Promise<void> => {
    const res = await apiClient.get(
      `${BASE}/stat-instance-steps/${stepId}/evidence/${evidenceId}/download`,
      { responseType: 'blob' },
    );
    const url = URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName || 'proof-of-compliance';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },

  skipStep: async (stepId: number, reason?: string): Promise<void> => {
    await apiClient.post(`${BASE}/stat-instance-steps/${stepId}/skip`, { reason: reason ?? null });
  },
};

export default statutoryService;
